import { HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { GbPaginatedResponse } from 'libs/gb-dashboard/core/data-access/src'
import { GbApiService } from 'libs/shared/data-access-core/src/lib/+state/gb-api.service'
import { Observable } from 'rxjs'
import { AuctionBlockListEntity, ImportUsersResponse, ProfileFilters, ProfilesEntity } from './profiles.models'
import { Params } from '@angular/router'
import { PreferredContactEnum } from 'libs/shared/models/profile/profile.models'

@Injectable({
  providedIn: 'root',
})
export class UsersApiService extends GbApiService {
  getProfiles(
    urlName: string,
    offset: number,
    filters?: ProfileFilters,
    limit: number = this.appConfig.itemsPageSize,
  ): Observable<GbPaginatedResponse<ProfilesEntity>> {
    let paramsObject: Params = { limit, offset, ...filters }

    if (filters?.invite_state) {
      paramsObject['invite_state'] = filters.invite_state.join(',')
    }

    // remove undefined values from the params object
    paramsObject = Object.entries(paramsObject).reduce((acc, [key, value]) => {
      if (value !== undefined) acc[key] = value
      return acc
    }, {} as Params)

    const params = new HttpParams({ fromObject: paramsObject })

    const url = `auctions/${urlName}/profiles/`
    return this.get<GbPaginatedResponse<ProfilesEntity>>(url, { params })
  }

  getProfile(urlName: string, profile: string): Observable<ProfilesEntity> {
    const url = `auctions/${urlName}/profiles/${profile}/`
    return this.get<ProfilesEntity>(url)
  }

  createProfile(urlName: string, profile: Partial<ProfilesEntity>): Observable<ProfilesEntity> {
    const url = `auctions/${urlName}/profiles/`
    return this.post<ProfilesEntity>(url, { ...profile, auction: urlName })
  }

  updateProfile(profile: Omit<ProfilesEntity, 'user'>): Observable<ProfilesEntity> {
    const url = `auctions/${profile.auction}/profiles/${profile.uuid}/`
    return this.patch<ProfilesEntity>(url, profile)
  }

  deleteUser(urlName: string, uuid: string): Observable<void> {
    const url = `auctions/${urlName}/profiles/${uuid}/`
    return this.delete<void>(url)
  }

  changePassword(password: string, confirm_password: string, user: string): Observable<unknown> {
    const url = `set_password/`
    return this.post<unknown>(url, {
      password: password,
      confirm_password: confirm_password,
      user: user,
    })
  }

  messageUser(urlName: string, profile: string, message_template: string): Observable<unknown> {
    const url = `auctions/${urlName}/profiles/${profile}/send-notification/`
    return this.post<unknown>(url, { message_template })
  }

  sendInvite(urlName: string, uuid: string): Observable<unknown> {
    const url = `auctions/${urlName}/profiles/${uuid}/send-invite/`
    return this.post<unknown>(url)
  }

  inviteUsersEmail(urlName: string, emails: string[]): Observable<ProfilesEntity[]> {
    const url = `auctions/${urlName}/profiles/send-email-invite/`
    return this.post(url, { emails })
  }

  inviteUsersPhone(urlName: string, phone_numbers: string[]): Observable<unknown> {
    const url = `auctions/${urlName}/profiles/send-phone-invite/`
    return this.post<unknown>(url, { phone_numbers })
  }

  sendVerification(
    urlName: string,
    profile: string,
    verification_type: PreferredContactEnum,
    phone_number?: string,
    email?: string,
    code_only?: boolean,
  ): Observable<ProfilesEntity[]> {
    const url = `auctions/${urlName}/profiles/${profile}/send-verification/`
    const paramsObject: Params = {}
    if (verification_type) {
      paramsObject['verification_type'] = verification_type
    }
    if (phone_number) {
      paramsObject['phone_number'] = phone_number
    }
    if (email) {
      paramsObject['email'] = email
    }
    if (code_only !== undefined) {
      paramsObject['code_only'] = code_only
    }
    return this.post(url, paramsObject)
  }

  importUsers(urlName: string, file: File): Observable<ImportUsersResponse> {
    const url = `auctions/${urlName}/profiles/import/`
    const formData = new FormData()
    formData.append('file', file, file.name)
    return this.post<ImportUsersResponse>(url, formData)
  }

  downloadUsersTemplate(urlName: string): Observable<Blob> {
    const url = `auctions/${urlName}/profiles/template/`
    return this.download(url)
  }

  getAuctionBlockList(
    urlName: string,
    offset: number,
    searchTerm?: string,
    limit: number = this.appConfig.itemsPageSize,
  ): Observable<GbPaginatedResponse<AuctionBlockListEntity>> {
    const paramsObject: Params = { limit, offset }

    const cleanedSearchTerm = searchTerm?.trim()
    if (cleanedSearchTerm) {
      paramsObject['search'] = cleanedSearchTerm
    }

    const params = new HttpParams({ fromObject: paramsObject })
    const url = `auctions/${urlName}/block-list/`
    return this.get<GbPaginatedResponse<AuctionBlockListEntity>>(url, { params })
  }

  createAuctionBlockListEntry(
    urlName: string,
    payload: Partial<AuctionBlockListEntity>,
  ): Observable<AuctionBlockListEntity> {
    const url = `auctions/${urlName}/block-list/`
    return this.post<AuctionBlockListEntity>(url, payload)
  }

  updateAuctionBlockListEntry(
    urlName: string,
    uuid: number,
    payload: Partial<AuctionBlockListEntity>,
  ): Observable<AuctionBlockListEntity> {
    const url = `auctions/${urlName}/block-list/${uuid}/`
    return this.patch<AuctionBlockListEntity>(url, payload)
  }

  deleteAuctionBlockListEntry(urlName: string, uuid: number): Observable<void> {
    const url = `auctions/${urlName}/block-list/${uuid}/`
    return this.delete<void>(url)
  }
}
